<template>
  <div class="metrics-picker-dialog__column-checkbox-wrapper">
    <div class="metrics-picker-dialog__column-checkbox metrics-picker-dialog__column-checkbox--label d-flex align-items-center">
      <ui-checkbox
        @change="selectedMetricsChanged({item,groupIndex})"
        :model-value="Boolean(item.export || selectedMetrics[item.index])"
        @update:model-value="onMetricCheckedUpdate"
        class="pt-4 mr-8"
        :planLimits="item.planLimits"
        :planLimitsEditable="selectedMetrics[item.index]"
        :label="reportMetricsName(item.value)"
        :disabled="item.disabled || item.export"
      ></ui-checkbox>
    </div>
    <div
      v-if="canEditCustomColumns && item.isCustomColumn"
      class="metrics-picker-dialog__hint-wrapper hint-switcher metrics-picker-dialog__column-checkbox--hint"
    >
      <div class="icon-container">
        <div class="edit align-center" @click="onAddNewColumn(item,groupIndex)">
          <ui-icon size="13" class="mr-4" icon="fas fa-pen"></ui-icon>
          {{t('Edit')}}
        </div>
        <div class="delete align-center" @click="onDelete(item, groupIndex)">
          <ui-icon size="13" :color="$colors.red" class="mr-4" icon="fas fa-trash-alt"></ui-icon>
          {{t('Delete')}}
        </div>
      </div>
      <div class="description" >
        <ui-tooltip-icon
          size="12"
          :color="$colors.green"
          class="mr-4"
          icon="fas fa-chart-line"
          :text="t('Shown on chart')"
          v-if="supportedMetricsAsObject[item.value]"
        />
        <span class="metrics-picker-dialog__hint" v-html="getHelp(item, true)" />
      </div>
    </div>

    <div v-else-if="item.helpFull" class="metrics-picker-dialog__hint-wrapper metrics-picker-dialog__column-checkbox--hint">
      <div>
        <ui-tooltip-icon
          size="12"
          :color="$colors.green"
          class="mr-4"
          icon="fas fa-chart-line"
         :text="t('Shown on chart')"
          v-if="supportedMetricsAsObject[item.value]"
        />
        <span class="metrics-picker-dialog__hint description" v-html="getHelp(item, true)" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useColors, useI18n } from '@/composables';
import { useStore } from '@/store';

interface Column {
  text: string;
  value: string;
  index: number;
  help: string;
  helpFull: string;
  disabled: boolean;
  selected: boolean;
  isCustomColumn: boolean;
  planLimits: keyof BillingLimits;
  export?: boolean;
}

const props = defineProps<{
  item: Column;
  groupIndex: number;
  getHelp: (item: Column, full: boolean) => string;
  onDelete: (item: Column, groupIndex: number) => void;
  reportMetricsName: (value: string) => string;
  onAddNewColumn: (item: Column, groupIndex: number) => void;
  selectedMetricsChanged: (payload: { item: Column; groupIndex: number }) => void;
  selectedMetrics: boolean[];
}>();

const { t } = useI18n();
const $colors = useColors();
const store = useStore();

const canEditCustomColumns = computed(() => {
  const user = store.user;
  return !(user.isMember && user.role === 'user');
});

const supportedMetricsAsObject = computed(() => store.getSupportedMetricsAsObject());

const onMetricCheckedUpdate = (value: boolean) => {
  if (!props.item.export) {
    props.selectedMetrics[props.item.index] = value;
  }
};
</script>

<style lang="scss" scoped>
.hint-switcher {
  position: relative;

  .icon-container {
    display: none;
    position: absolute;
    left: 0;
    top: 0;
    bottom: 4px;
    align-items: center;
  }

  &:hover {
    .icon-container {
      display: flex;
    }

    .description {
      visibility: hidden;
    }
  }
}

.icon-container {
  .edit,
  .delete {
    display: flex;
    cursor: pointer;
  }

  .edit {
    color: var(--color-graphite);
  }

  .delete {
    margin-left: 10px;
    color: var(--color-red);
  }
}
</style>

<style lang="scss">
.metrics-picker-dialog__column-checkbox-wrapper {
  display: flex;

  .metrics-picker-dialog__column-checkbox--label {
    flex: 0 0 33.333333%;
    max-width: 33.333333%;
    min-width: 0;
  }

  .metrics-picker-dialog__column-checkbox--hint {
    flex: 1;
    min-width: 0;
  }
}
</style>
